import "./index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import AuthCallback from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Visibility from "@/pages/Visibility";
import Content from "@/pages/Content";
import ProfileTab from "@/pages/ProfileTab";
import Pricing from "@/pages/Pricing";
import Settings from "@/pages/Settings";
import PublicProfile from "@/pages/PublicProfile";
import ReportCard from "@/pages/ReportCard";
import ProfessionLanding from "@/pages/ProfessionLanding";
import AdminDashboard from "@/pages/AdminDashboard";
import GuidePage from "@/pages/GuidePage";
import ReviewCoaching from "@/pages/ReviewCoaching";
import Optimize from "@/pages/Optimize";
import GapCloser from "@/pages/GapCloser";
import Alerts from "@/pages/Alerts";
import Competitors from "@/pages/Competitors";
import MarketIntel from "@/pages/MarketIntel";
import ReferralNetwork from "@/pages/ReferralNetwork";
import DigestPreview from "@/pages/DigestPreview";
import AccuracyDashboard from "@/pages/AccuracyDashboard";
import AIInsights from "@/pages/AIInsights";
import AEOTemplates from "@/pages/AEOTemplates";
import DirectoryAutoFix from "@/pages/DirectoryAutoFix";
import LeadTracker from "@/pages/LeadTracker";
import ActionCenter from "@/pages/ActionCenter";
import PresenceBuilder from "@/pages/PresenceBuilder";
import TermsOfService from "@/pages/TermsOfService";
import { AlertTriangle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Config context for scan mode
const ConfigContext = createContext({ scanMode: null });
export function useConfig() { return useContext(ConfigContext); }

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFBF7' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect session_id during render, not in useEffect
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/visibility" element={<ProtectedRoute><Visibility /></ProtectedRoute>} />
      <Route path="/content" element={<ProtectedRoute><Content /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfileTab /></ProtectedRoute>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/p/:slug" element={<PublicProfile />} />
      <Route path="/report/:reportId" element={<ReportCard />} />
      <Route path="/for/:slug" element={<ProfessionLanding />} />
      <Route path="/for/:slug/:sub" element={<ProfessionLanding />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/guides/:platform" element={<GuidePage />} />
      <Route path="/review/:requestId" element={<ReviewCoaching />} />
      <Route path="/optimize" element={<Optimize />} />
      <Route path="/gaps" element={<GapCloser />} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/competitors" element={<ProtectedRoute><Competitors /></ProtectedRoute>} />
      <Route path="/market-intel" element={<ProtectedRoute><MarketIntel /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><ReferralNetwork /></ProtectedRoute>} />
      <Route path="/digest-preview" element={<ProtectedRoute><DigestPreview /></ProtectedRoute>} />
      <Route path="/accuracy" element={<ProtectedRoute><AccuracyDashboard /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><AIInsights /></ProtectedRoute>} />
      <Route path="/aeo-templates" element={<ProtectedRoute><AEOTemplates /></ProtectedRoute>} />
      <Route path="/directories" element={<ProtectedRoute><DirectoryAutoFix /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadTracker /></ProtectedRoute>} />
      <Route path="/actions" element={<ProtectedRoute><ActionCenter /></ProtectedRoute>} />
      <Route path="/presence" element={<ProtectedRoute><PresenceBuilder /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [scanMode, setScanMode] = useState(null);

  useEffect(() => {
    axios.get(`${API}/config`).then(r => setScanMode(r.data.scan_mode)).catch(() => {});
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ConfigContext.Provider value={{ scanMode }}>
          <BrandingProvider>
            <AuthProvider>
              <AppRouter />
            </AuthProvider>
          </BrandingProvider>
        </ConfigContext.Provider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

function TestModeBanner() {
  return (
    <div className="px-4 py-2 text-center text-xs font-medium flex items-center justify-center gap-1.5 z-[100] relative" style={{ backgroundColor: '#F7F4EF', color: '#C67A5C', borderBottom: '1px solid #D4C9B8' }} data-testid="test-mode-banner">
      <AlertTriangle size={13} strokeWidth={2} />
      Test mode — limited scan queries
    </div>
  );
}

export default App;
